import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { useCurrentUserId } from 'modules/Auth/hooks';
import { queryKeys, useMutationTE } from 'utils/react-query';
import { editCreditCardTE, EditCreditCardPayload } from 'api/users/creditCards';
import { errorToReactLeft } from 'utils/fp';
import { ReactLeft } from 'utils/uiStates/uiStates';

interface CreditCardOption {
  value: string;
  name: string;
  content: React.ReactNode;
  metaData?: CF.API.Users.Card;
}

export const useEditCreditCard = (onConfirm?: () => void) => {
  const userId = useCurrentUserId();
  const queryClient = useQueryClient();

  const handleSuccess = useCallback((): void => {
    cogoToast.success('Payment method updated successfully. Proceeding with auto-recharge setup...');
    queryClient.invalidateQueries([queryKeys.UserCreditCards, { userId }]);
    onConfirm?.();
  }, [queryClient, userId, onConfirm]);

  const handleError = useCallback((error: ReactLeft): void => {
    cogoToast.error(error.props?.reason || 'Failed to update payment method. Please try again.', { heading: error.props?.title });
  }, []);

  const onSettled = useCallback(() => {
    queryClient.invalidateQueries([queryKeys.UserCreditCards, { userId }]);
  }, [queryClient, userId]);

  const mutation = useMutationTE((cardData: EditCreditCardPayload) => editCreditCardTE({ userId, card: cardData }, errorToReactLeft)(), {
    onSuccess: handleSuccess,
    onError: handleError,
    onSettled,
  });

  const updateCreditCardIfNeeded = useCallback(
    async (credit?: CreditCardOption): Promise<void> => {
      if (!credit) {
        return Promise.resolve();
      }

      const isDefaultCard = credit.metaData?.default ?? false;

      if (!isDefaultCard && credit.metaData) {
        const updateableCardValues: EditCreditCardPayload = {
          id: credit.metaData.id,
          default: true,
          name: credit.metaData.name,
        };

        return new Promise<void>((resolve, reject) => {
          mutation.mutate(updateableCardValues, {
            onSuccess: () => {
              resolve();
            },
            onError: (error) => {
              reject(error);
            },
          });
        });
      }

      return Promise.resolve();
    },
    [mutation],
  );

  return {
    ...mutation,
    updateCreditCardIfNeeded,
  };
};
